import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { RETENTION_ANSWER } from '@wodi/domain';
import {
  IDENTITY_EVIDENCE,
  type RecordExport,
  type RecordsRequestView,
  type VerifyIdentityBody,
} from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  printSheet,
  RadioGroup,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useEffect, useRef, useState } from 'react';

import {
  changeCandidatesQuery,
  closeRecordsRequest,
  linkChange,
  recordsRequestQuery,
  releaseRecord,
  reviewCorrection,
  verifyIdentity,
} from '../api/records.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant, formatIsoDate } from '../patients/format.ts';
import { toasts } from '../toasts.ts';
import {
  actionWords,
  AREA_WORDS,
  CHANNEL_WORDS,
  EVIDENCE_WORDS,
  FORMAT_WORDS,
  KIND_WORDS,
  OUTCOME_WORDS,
  RELATIONSHIP_WORDS,
  stateTag,
} from './labels.ts';
import { RecordSummarySheet } from './record-summary.tsx';
import { errorText, LoadFailed } from './records-page.tsx';
import styles from './records.module.css';

// One data-subject request (docs/plans/phase2-records-data-rights.md §6): the facts, the
// identity check, then by kind: prepare the record (access), link the correction made
// (correction), the retention explanation (deletion); and close with the response given.

const route = getRouteApi('/_app/records/requests/$requestId');

type Evidence = VerifyIdentityBody['evidence'];
type Outcome = RecordsRequestView['allowedOutcomes'][number];

function useRefresh(requestId: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['records'] });
    await queryClient.invalidateQueries({ queryKey: ['records', 'request', requestId] });
  };
}

function IdentityDialog({
  request,
  onClose,
}: {
  request: RecordsRequestView;
  onClose: () => void;
}) {
  const online = useOnline();
  const refresh = useRefresh(request.id);
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [note, setNote] = useState('');
  const [authority, setAuthority] = useState('');
  const forSomeoneElse = request.requesterRelationship !== 'self';
  const verify = useMutation({
    mutationFn: () =>
      verifyIdentity(request.id, {
        version: request.version,
        evidence: evidence ?? 'other',
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        ...(forSomeoneElse ? { authorityNote: authority.trim() } : {}),
      }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Identity checked', description: request.number });
      onClose();
    },
  });
  const ready =
    online &&
    evidence !== null &&
    (evidence !== 'other' || note.trim() !== '') &&
    (!forSomeoneElse || authority.trim() !== '');
  return (
    <Dialog
      title="Record identity check"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RadioGroup
        label={
          forSomeoneElse ? 'What proved who the requester is?' : 'What proved who the patient is?'
        }
        description="Provisional list, pending legal review."
        options={IDENTITY_EVIDENCE.map((e) => ({ id: e, label: EVIDENCE_WORDS[e] }))}
        value={evidence}
        onChange={(v) => {
          setEvidence(v as Evidence);
        }}
      />
      <TextField
        label={evidence === 'other' ? 'What was seen' : 'Note (optional)'}
        value={note}
        onChange={setNote}
      />
      {forSomeoneElse ? (
        <TextField
          label="How was their authority to ask for the patient checked?"
          description="For example the document that shows they are the parent, guardian or representative."
          value={authority}
          onChange={setAuthority}
        />
      ) : null}
      {verify.error === null ? null : (
        <Notice tone="error">
          {errorText(verify.error, "The check wasn't saved. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={verify.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            verify.mutate();
          }}
        >
          Record identity check
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function downloadExport(data: RecordExport): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `wodi-record-${data.record.patient.patientNumber}-${data.generatedAt.slice(0, 10)}.json`;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

type Format = 'printed_summary' | 'machine_readable';

function PrepareDialog({
  request,
  onClose,
  onPrinted,
}: {
  request: RecordsRequestView;
  onClose: () => void;
  onPrinted: (data: RecordExport) => void;
}) {
  const online = useOnline();
  const refresh = useRefresh(request.id);
  const [format, setFormat] = useState<Format>('printed_summary');
  const [recipient, setRecipient] = useState(
    request.requesterRelationship === 'self' ? 'The patient' : (request.requesterName ?? ''),
  );
  const [reason, setReason] = useState(`Access request ${request.number}`);
  const prepare = useMutation({
    mutationFn: () =>
      releaseRecord(request.id, { format, recipient: recipient.trim(), reason: reason.trim() }),
    onSuccess: async (result) => {
      await refresh();
      toasts.show({ title: 'Record released', description: FORMAT_WORDS[result.release.format] });
      if (result.release.format === 'machine_readable') downloadExport(result.export);
      else onPrinted(result.export);
      onClose();
    },
  });
  const ready = online && recipient.trim() !== '' && reason.trim().length >= 5;
  return (
    <Dialog
      title="Prepare record"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RadioGroup
        label="As"
        options={[
          {
            id: 'printed_summary',
            label: FORMAT_WORDS.printed_summary,
            description: 'A4, to print or save as PDF',
          },
          {
            id: 'machine_readable',
            label: FORMAT_WORDS.machine_readable,
            description: 'For another health system: a FHIR R4 bundle and the full record',
          },
        ]}
        value={format}
        onChange={(v) => {
          setFormat(v as Format);
        }}
      />
      <TextField label="Released to" value={recipient} onChange={setRecipient} />
      <TextField
        label="Reason"
        description="At least 5 characters. Kept in the release log and the audit trail."
        value={reason}
        onChange={setReason}
      />
      <p className={styles.meta}>
        The copy holds only records you are cleared to see. Releasing is logged with a fingerprint
        of exactly what was given.
      </p>
      {prepare.error === null ? null : (
        <Notice tone="error">
          {errorText(prepare.error, "The record wasn't prepared. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={prepare.isPending}
          pendingLabel="Preparing…"
          onPress={() => {
            prepare.mutate();
          }}
        >
          {format === 'printed_summary' ? 'Print record' : 'Download record'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LinkChangeDialog({
  request,
  onClose,
}: {
  request: RecordsRequestView;
  onClose: () => void;
}) {
  const online = useOnline();
  const refresh = useRefresh(request.id);
  const candidates = useQuery(changeCandidatesQuery(request.id));
  const linked = new Set(request.changes.map((c) => c.id));
  const link = useMutation({
    mutationFn: (auditEventId: string) => linkChange(request.id, auditEventId),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Change linked', description: request.number });
      onClose();
    },
  });
  return (
    <Dialog
      title="Link change"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        Corrections, amendments and entered-in-error marks on this patient's record since the
        request was received.
      </p>
      {candidates.isPending ? <LoadingBlock label="Loading changes" rows={3} /> : null}
      {candidates.isError ? (
        <LoadFailed
          error={candidates.error}
          fallback="Changes couldn't be loaded."
          onRetry={() => void candidates.refetch()}
        />
      ) : null}
      {candidates.isSuccess && candidates.data.items.length === 0 ? (
        <p>
          No corrections since the request came in. Make the correction first, then link it here.
        </p>
      ) : null}
      {candidates.isSuccess && candidates.data.items.length > 0 ? (
        <ul className={styles.candidates} aria-label="Changes to link">
          {candidates.data.items.map((c) => (
            <li key={c.id} className={styles.candidate}>
              <span>
                {actionWords(c.action)}, {c.actor.displayName}, {formatInstant(c.occurredAt)}
              </span>
              {linked.has(c.id) ? (
                <Tag state="done">Linked</Tag>
              ) : (
                <Button
                  dense
                  isDisabled={!online}
                  isPending={link.isPending && link.variables === c.id}
                  pendingLabel="Linking…"
                  onPress={() => {
                    link.mutate(c.id);
                  }}
                >
                  Link
                </Button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {link.error === null ? null : (
        <Notice tone="error">
          {errorText(link.error, "The change wasn't linked. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Accept a correction, to be made as a normal amendment, or decline it with a note that
 * closes the request (docs/plans/golive-privacy-tooling.md §5.2).
 */
function ReviewDialog({ request, onClose }: { request: RecordsRequestView; onClose: () => void }) {
  const online = useOnline();
  const refresh = useRefresh(request.id);
  const [decision, setDecision] = useState<'accepted' | 'declined' | null>(null);
  const [note, setNote] = useState('');
  const review = useMutation({
    mutationFn: () =>
      reviewCorrection(request.id, {
        version: request.version,
        decision: decision ?? 'accepted',
        note: note.trim(),
      }),
    onSuccess: async () => {
      await refresh();
      toasts.show({
        title: decision === 'declined' ? 'Correction declined' : 'Correction accepted',
        description: request.number,
      });
      onClose();
    },
  });
  const ready = online && decision !== null && note.trim().length >= 5;
  return (
    <Dialog
      title="Review correction"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RadioGroup
        label="Decision"
        options={[
          { id: 'accepted', label: 'Accept: the record is wrong and will be corrected' },
          { id: 'declined', label: 'Decline: the record stays as it is' },
        ]}
        value={decision}
        onChange={(v) => {
          if (v === 'accepted' || v === 'declined') setDecision(v);
        }}
      />
      <TextArea
        label={decision === 'declined' ? 'Why, as told to the requester' : 'What will be corrected'}
        description={
          decision === 'declined'
            ? 'Declining closes the request with this as the response.'
            : 'At least 5 characters. Kept with the request.'
        }
        value={note}
        onChange={setNote}
        rows={3}
      />
      {review.error === null ? null : (
        <Notice tone="error">
          {errorText(review.error, "The review wasn't saved. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={review.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            review.mutate();
          }}
        >
          {decision === 'declined' ? 'Decline correction' : 'Accept correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CloseDialog({ request, onClose }: { request: RecordsRequestView; onClose: () => void }) {
  const online = useOnline();
  const refresh = useRefresh(request.id);
  const [outcome, setOutcome] = useState<Outcome | null>(
    request.kind === 'deletion' ? 'declined' : null,
  );
  const [response, setResponse] = useState(request.kind === 'deletion' ? RETENTION_ANSWER : '');
  const close = useMutation({
    mutationFn: () =>
      closeRecordsRequest(request.id, {
        version: request.version,
        outcome: outcome ?? 'withdrawn',
        response: response.trim(),
      }),
    onSuccess: async () => {
      await refresh();
      toasts.show({ title: 'Request closed', description: request.number });
      onClose();
    },
  });
  const ready = online && outcome !== null && response.trim().length >= 5;
  return (
    <Dialog
      title="Close request"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RadioGroup
        label="Outcome"
        options={request.allowedOutcomes.map((o) => ({ id: o, label: OUTCOME_WORDS[o] }))}
        value={outcome}
        onChange={(v) => {
          setOutcome(v as Outcome);
        }}
      />
      <TextArea
        label="What the requester was told"
        description={
          request.kind === 'deletion'
            ? 'The retention explanation is provisional, pending legal review.'
            : 'At least 5 characters. Kept with the request.'
        }
        value={response}
        onChange={setResponse}
        rows={4}
      />
      {close.error === null ? null : (
        <Notice tone="error">
          {errorText(close.error, "The request wasn't closed. Try again.")}
        </Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={close.isPending}
          pendingLabel="Closing…"
          onPress={() => {
            close.mutate();
          }}
        >
          Close request
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Facts({ request }: { request: RecordsRequestView }) {
  return (
    <dl className={styles.facts}>
      <dt>Kind</dt>
      <dd>
        {KIND_WORDS[request.kind]}
        {request.correctionArea === null ? '' : `: ${AREA_WORDS[request.correctionArea]}`}
      </dd>
      <dt>Asked by</dt>
      <dd>
        {RELATIONSHIP_WORDS[request.requesterRelationship]}
        {request.requesterName === null ? '' : `, ${request.requesterName}`}
      </dd>
      <dt>Arrived</dt>
      <dd>
        {CHANNEL_WORDS[request.channel]}, {formatInstant(request.receivedAt)}
      </dd>
      <dt>Due by</dt>
      <dd>{formatIsoDate(request.dueOn)} (provisional)</dd>
      <dt>Logged by</dt>
      <dd>{request.receivedBy.displayName}</dd>
      <dt>Asked for</dt>
      <dd className={styles.prose}>{request.details}</dd>
    </dl>
  );
}

function RequestView({ request }: { request: RecordsRequestView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [dialog, setDialog] = useState<'identity' | 'prepare' | 'link' | 'close' | 'review' | null>(
    null,
  );
  const [printed, setPrinted] = useState<RecordExport | null>(null);
  const sheet = useRef<HTMLElement>(null);
  const mayManage = session !== null && can(session, 'records.requests.manage');
  const mayExport = session !== null && can(session, 'records.export');
  const open = request.status !== 'closed';
  const verified = request.status === 'verified';
  // Verified means open: a closed request is never verified.
  const canPrepare = mayExport && verified && request.kind === 'access';
  const mayReadLog = session !== null && can(session, 'records.access_log.read');
  const accepted = request.review?.decision === 'accepted';
  const canLink = mayManage && mayReadLog && verified && request.kind === 'correction' && accepted;
  const canReview =
    session !== null && can(session, 'records.corrections.review') && request.awaitingReview;
  const tag = stateTag(request);

  useEffect(() => {
    if (printed !== null && sheet.current !== null) printSheet(sheet.current);
  }, [printed]);
  useShortcut(
    'p',
    outsideDialogs(() => {
      if (canPrepare && online) setDialog('prepare');
    }),
    { enabled: canPrepare, description: 'Prepare record' },
  );
  useShortcut(
    'l',
    outsideDialogs(() => {
      if (canLink && online) setDialog('link');
    }),
    { enabled: canLink, description: 'Link change' },
  );

  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={request.patient.id} />
      <PageHeader
        title={`Records request ${request.number}`}
        description={`${KIND_WORDS[request.kind]} request.`}
        actions={<Tag state={tag.state}>{tag.word}</Tag>}
      />
      {!online ? (
        <Notice tone="info">
          No connection to Wodi. What you see was loaded earlier; changes wait for the connection.
        </Notice>
      ) : null}
      <Section title="The request">
        <Facts request={request} />
      </Section>
      <Section
        title="Identity"
        {...(request.identity === null && request.status === 'received' && mayManage
          ? {
              actions: (
                <Button
                  isDisabled={!online}
                  onPress={() => {
                    setDialog('identity');
                  }}
                >
                  Record identity check
                </Button>
              ),
            }
          : {})}
      >
        {request.identity === null ? (
          <p className={styles.meta}>
            Not checked yet. Nothing is released or changed until the requester's identity is
            checked.
          </p>
        ) : (
          <dl className={styles.facts}>
            <dt>Evidence</dt>
            <dd>
              {EVIDENCE_WORDS[request.identity.evidence]}
              {request.identity.note === null ? '' : `: ${request.identity.note}`}
            </dd>
            {request.identity.authorityNote === null ? null : (
              <>
                <dt>Authority</dt>
                <dd>{request.identity.authorityNote}</dd>
              </>
            )}
            <dt>Checked by</dt>
            <dd>
              {request.identity.verifiedBy.displayName},{' '}
              {formatInstant(request.identity.verifiedAt)}
            </dd>
          </dl>
        )}
      </Section>
      {request.kind === 'access' ? (
        <Section
          title="Releases"
          {...(canPrepare
            ? {
                actions: (
                  <Button
                    variant="primary"
                    isDisabled={!online}
                    onPress={() => {
                      setDialog('prepare');
                    }}
                  >
                    Prepare record
                  </Button>
                ),
              }
            : {})}
        >
          {canPrepare ? <ShortcutHint combo="p" action="prepare the record" /> : null}
          {!mayExport && open ? (
            <p className={styles.meta}>
              Preparing the record needs the export permission. Ask the medical records office.
            </p>
          ) : null}
          <TableScroll>
            <Table flat aria-label="Releases for this request">
              <TableHeader>
                <Column isRowHeader>Released</Column>
                <Column>Format</Column>
                <Column>To</Column>
                <Column>By</Column>
                <Column>Fingerprint</Column>
              </TableHeader>
              <TableBody
                items={request.releases}
                renderEmptyState={() =>
                  verified || !open
                    ? 'Nothing released for this request.'
                    : 'Check identity first, then prepare the record.'
                }
              >
                {(r) => (
                  <Row id={r.id}>
                    <Cell>{formatInstant(r.releasedAt)}</Cell>
                    <Cell>{FORMAT_WORDS[r.format]}</Cell>
                    <Cell>{r.recipient}</Cell>
                    <Cell>{r.releasedBy.displayName}</Cell>
                    <Cell>
                      <span className="wodi-id" title={r.contentHash}>
                        {r.contentHash.slice(0, 12)}
                      </span>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        </Section>
      ) : null}
      {request.kind === 'correction' ? (
        <Section
          title="Correction"
          {...(canLink
            ? {
                actions: (
                  <Button
                    isDisabled={!online}
                    onPress={() => {
                      setDialog('link');
                    }}
                  >
                    Link change
                  </Button>
                ),
              }
            : {})}
        >
          {request.review === null ? (
            <div className={styles.actions}>
              <p className={styles.prose}>
                {verified
                  ? 'Waiting for review. Someone cleared to review corrections accepts it, to be made as a normal amendment, or declines it with a reason.'
                  : 'Reviewed once the identity check is recorded.'}
              </p>
              {canReview ? (
                <Button
                  isDisabled={!online}
                  onPress={() => {
                    setDialog('review');
                  }}
                >
                  Review correction
                </Button>
              ) : null}
            </div>
          ) : (
            <dl className={styles.facts}>
              <dt>Review</dt>
              <dd>
                {`${request.review.decision === 'accepted' ? 'Accepted' : 'Declined'} by ${request.review.reviewedBy.displayName}, ${formatInstant(request.review.reviewedAt)}`}
              </dd>
              <dt>Note</dt>
              <dd className={styles.prose}>{request.review.note}</dd>
            </dl>
          )}
          <p className={styles.prose}>
            {request.correctionArea === 'demographics'
              ? 'Correct personal details on the patient record with Correct details: each change keeps the earlier version in the history.'
              : 'Correct the clinical record from the visit concerned: amend the note, or mark a diagnosis, problem, vital sign or result entered in error with the reason. Nothing is deleted; history stays viewable.'}{' '}
            <Link to="/patients/$patientId" params={{ patientId: request.patient.id }}>
              Open patient record
            </Link>
          </p>
          {canLink ? <ShortcutHint combo="l" action="link the change made" /> : null}
          <TableScroll>
            <Table flat aria-label="Changes linked to this request">
              <TableHeader>
                <Column isRowHeader>When</Column>
                <Column>Change</Column>
                <Column>By</Column>
              </TableHeader>
              <TableBody items={request.changes} renderEmptyState={() => 'No change linked yet.'}>
                {(c) => (
                  <Row id={c.id}>
                    <Cell>{formatInstant(c.occurredAt)}</Cell>
                    <Cell>{actionWords(c.action)}</Cell>
                    <Cell>{c.actor.displayName}</Cell>
                  </Row>
                )}
              </TableBody>
            </Table>
          </TableScroll>
        </Section>
      ) : null}
      {request.kind === 'deletion' ? (
        <Section title="Retention">
          <p className={styles.prose}>{RETENTION_ANSWER}</p>
          <p className={styles.meta}>Provisional wording, pending legal review.</p>
        </Section>
      ) : null}
      <Section
        title="Outcome"
        {...(open && mayManage
          ? {
              actions: (
                <Button
                  isDisabled={!online}
                  onPress={() => {
                    setDialog('close');
                  }}
                >
                  Close request
                </Button>
              ),
            }
          : {})}
      >
        {request.status === 'closed' && request.outcome !== null ? (
          <dl className={styles.facts}>
            <dt>Outcome</dt>
            <dd>{OUTCOME_WORDS[request.outcome]}</dd>
            <dt>Told the requester</dt>
            <dd className={styles.prose}>{request.response}</dd>
            <dt>Closed by</dt>
            <dd>
              {request.closedBy?.displayName}
              {request.closedAt === null ? '' : `, ${formatInstant(request.closedAt)}`}
            </dd>
          </dl>
        ) : (
          <p className={styles.meta}>Open. Close it with the response given to the requester.</p>
        )}
      </Section>
      {printed === null ? null : (
        <Section title="Printed summary">
          <div className={styles.actions}>
            <Button
              onPress={() => {
                if (sheet.current !== null) printSheet(sheet.current);
              }}
            >
              Print again
            </Button>
            <p className={styles.meta}>
              Printing again gives the same copy; it is not logged again.
            </p>
          </div>
          <div className={styles.preview}>
            <RecordSummarySheet data={printed} requestNumber={request.number} ref={sheet} />
          </div>
        </Section>
      )}
      {dialog === 'identity' ? (
        <IdentityDialog
          request={request}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'prepare' ? (
        <PrepareDialog
          request={request}
          onClose={() => {
            setDialog(null);
          }}
          onPrinted={setPrinted}
        />
      ) : null}
      {dialog === 'link' ? (
        <LinkChangeDialog
          request={request}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'review' ? (
        <ReviewDialog
          request={request}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'close' ? (
        <CloseDialog
          request={request}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </div>
  );
}

export function RecordsRequestPage() {
  const { requestId } = route.useParams();
  const request = useQuery(recordsRequestQuery(requestId));
  if (request.isPending) return <LoadingBlock label="Loading the request" rows={5} />;
  if (request.isError)
    return (
      <LoadFailed
        error={request.error}
        fallback="The request couldn't be loaded."
        onRetry={() => void request.refetch()}
      />
    );
  return <RequestView request={request.data} />;
}
